import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  AlertTriangle, BedDouble, Building2, Download, Gauge, HeartPulse,
  Pill, Printer, TrendingUp, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';
import PrezziFarmaciDialog from '@/components/PrezziFarmaciDialog';

const PERIODI = [
  { mesi: 1, label: 'Mese corrente' },
  { mesi: 3, label: '3 mesi' },
  { mesi: 6, label: '6 mesi' },
  { mesi: 12, label: '12 mesi' },
];
const TORTA = ['#0f766e', '#0284c7', '#b45309', '#7c3aed', '#dc2626', '#059669', '#d97706', '#475569'];
const asseX = { dataKey: 'etichetta', tick: { fontSize: 11, fill: '#64748b' }, interval: 0 };
const asseY = { tick: { fontSize: 11, fill: '#64748b' }, allowDecimals: false };
const num = (v, suffisso = '') => (v === null || v === undefined ? 'n.d.' : `${v}${suffisso}`);
const euro = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dataIt = (v) => (v ? new Date(v).toLocaleDateString('it-IT') : '-');

const Kpi = ({ icona: Icona, titolo, valore, dettaglio, barra = 'bg-brand', testId }) => (
  <div data-testid={testId}
    className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:border-slate-300">
    <div className={`absolute inset-x-0 top-0 h-[3px] ${barra}`} />
    <div className="flex items-start justify-between gap-2">
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{titolo}</p>
      <Icona className="w-4 h-4 text-slate-300 transition-colors group-hover:text-brand" />
    </div>
    <p className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{valore}</p>
    {dettaglio && <p className="mt-0.5 text-xs text-slate-500 leading-snug">{dettaglio}</p>}
  </div>
);

const Riquadro = ({ titolo, nota, children, testId, className = '' }) => (
  <Card data-testid={testId} className={className}>
    <CardHeader className="pb-1">
      <CardTitle className="text-sm font-semibold text-slate-800">{titolo}</CardTitle>
      {nota && <p className="text-[11px] text-slate-400">{nota}</p>}
    </CardHeader>
    <CardContent className="pt-2 min-h-[120px]">{children}</CardContent>
  </Card>
);

export default function CruscottoDirezionale() {
  const [mesi, setMesi] = useState(1);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prezziAperti, setPrezziAperti] = useState(false);

  const carica = useCallback(() => {
    setLoading(true);
    api.get('/direzione/cruscotto', { params: { mesi } })
      .then(r => setD(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Errore nel caricamento'))
      .finally(() => setLoading(false));
  }, [mesi]);

  useEffect(() => { carica(); }, [carica]);

  const esportaCsv = async () => {
    try {
      const r = await api.get('/direzione/export.csv', { params: { mesi }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cruscotto_direzionale_${mesi}m.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Esportazione pronta');
    } catch (e) { toast.error('Esportazione non riuscita'); }
  };

  const stampa = () => {
    if (!d) return;
    const o = d.occupazione, m = d.movimenti, f = d.farmaci, c = d.clinici, ca = d.carico_assistenziale;
    const riga = (k, v) => `<tr><td style="font-size:11px">${k}</td><td align="right" style="font-size:11px"><b>${v}</b></td></tr>`;
    const tabella = (titolo, righe) => `
      <h3 style="margin:14px 0 4px;font-size:13px">${titolo}</h3>
      <table style="width:100%;border-collapse:collapse" border="1" cellpadding="4"><tbody>${righe}</tbody></table>`;
    openPrintWindow(`Cruscotto direzionale ${d.periodo.etichetta}`, `
      <h2>Cruscotto direzionale</h2>
      <p><b>Periodo:</b> ${d.periodo.etichetta} (dal ${dataIt(d.periodo.da)} al ${dataIt(d.periodo.a)})
      &nbsp;&nbsp;<b>Sedi:</b> ${d.sedi.map(s => s.nome).join(', ') || '-'}</p>
      ${tabella('Occupazione', [
        riga('Posti letto', o.posti_letto), riga('Ospiti presenti', o.ospiti_presenti),
        riga('Posti liberi', o.posti_liberi), riga('Occupazione', `${o.occupazione_pct}%`),
        riga('Giornate di degenza', o.giornate_degenza),
        riga('Tasso di occupazione del periodo', `${o.tasso_occupazione_pct}%`),
        riga('Degenza media', `${o.degenza_media_giorni} giorni`)].join(''))}
      ${tabella('Movimenti', [riga('Ingressi', m.ingressi), riga('Dimissioni', m.dimessi),
        riga('Decessi', m.decessi), riga('Trasferimenti', m.trasferiti), riga('Saldo', m.saldo)].join(''))}
      ${tabella('Farmaci', [riga('Somministrazioni registrate', f.somministrazioni),
        riga('Aderenza', num(f.aderenza_pct, '%')), riga('Unita consumate', f.unita_consumate),
        riga('Terapie attive', f.terapie_attive), riga('Spesa stimata', euro(f.spesa_stimata)),
        riga('Farmaci senza prezzo in anagrafica', f.farmaci_senza_prezzo)].join(''))}
      ${tabella('Indicatori clinici', [riga('Cadute', `${c.cadute} (${c.cadute_per_1000}/1000 giornate)`),
        riga('Eventi avversi', `${c.eventi_avversi} (${c.eventi_per_1000}/1000 giornate)`),
        riga('Lesioni insorte', `${c.lesioni_nuove} (${c.lesioni_per_1000}/1000 giornate)`),
        riga('Lesioni attive', c.lesioni_attive),
        riga('Contenzioni attive', `${c.contenzioni_attive} (${c.contenzioni_prevalenza_pct}%)`)].join(''))}
      ${tabella('Carico assistenziale', [riga('Ospiti con Barthel valutato', `${ca.barthel_valutati} su ${ca.ospiti}`),
        riga('Barthel medio', num(ca.barthel_medio)), riga('Dipendenza alta', ca.dipendenza_alta),
        riga('Dipendenza media', ca.dipendenza_media), riga('Autonomi', ca.autonomi),
        riga('Minuti stimati per ospite al giorno', ca.minuti_per_ospite),
        riga('Ore assistenza stimate al giorno', ca.ore_stimate_giorno)].join(''))}
      ${tabella('Confronto sedi', d.confronto_sedi.map(r =>
        riga(r.sede, `${r.presenti}/${r.posti_letto} posti · ${r.occupazione_pct}% · cadute ${r.cadute_per_1000}/1000 · aderenza ${num(r.aderenza_pct, '%')}`)).join(''))}
      <p style="font-size:10px;margin-top:18px">Documento generato il ${dataIt(d.generato_il)}. I minuti di assistenza sono una stima basata sull'indice di Barthel.</p>
    `);
  };

  if (loading && !d) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>;
  if (!d) return null;

  const o = d.occupazione, m = d.movimenti, f = d.farmaci, c = d.clinici, ca = d.carico_assistenziale;
  const movimentiSerie = [
    { nome: 'Ingressi', valore: m.ingressi }, { nome: 'Dimissioni', valore: m.dimessi },
    { nome: 'Decessi', valore: m.decessi }, { nome: 'Trasferimenti', valore: m.trasferiti },
  ];

  return (
    <div className="space-y-5 max-w-[1600px] mx-auto" data-testid="cruscotto-direzionale-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Cruscotto Direzionale</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {d.periodo.etichetta} · dal {dataIt(d.periodo.da)} al {dataIt(d.periodo.a)} · {d.sedi.length} sede/i
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
            {PERIODI.map(p => (
              <button key={p.mesi} onClick={() => setMesi(p.mesi)} data-testid={`periodo-${p.mesi}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${mesi === p.mesi ? 'bg-brand text-white' : 'text-slate-500 hover:text-brand'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setPrezziAperti(true)} data-testid="prezzi-farmaci-btn">
            <Pill className="w-4 h-4 mr-1.5" />Prezzi farmaci
          </Button>
          <Button variant="outline" size="sm" onClick={esportaCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1.5" />CSV
          </Button>
          <Button size="sm" onClick={stampa} data-testid="stampa-btn">
            <Printer className="w-4 h-4 mr-1.5" />Stampa
          </Button>
        </div>
      </div>

      {(o.senza_data_ingresso > 0 || o.ospiti_presenti > o.posti_letto) && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="avviso-dati">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800 space-y-0.5">
            {o.ospiti_presenti > o.posti_letto && (
              <p>Gli ospiti presenti ({o.ospiti_presenti}) superano i posti letto registrati ({o.posti_letto}): aggiorna i posti letto delle sedi in Impostazioni.</p>
            )}
            {o.senza_data_ingresso > 0 && (
              <p>{o.senza_data_ingresso} anagrafiche senza data di ingresso: le giornate di degenza sono stimate sull'intero periodo.</p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi icona={BedDouble} titolo="Occupazione" valore={`${o.occupazione_pct}%`} testId="kpi-occupazione"
          dettaglio={`${o.ospiti_presenti} presenti su ${o.posti_letto} posti · ${o.posti_liberi} liberi`} />
        <Kpi icona={Gauge} titolo="Giornate di degenza" valore={o.giornate_degenza.toLocaleString('it-IT')} testId="kpi-giornate"
          dettaglio={`${o.tasso_occupazione_pct}% delle giornate disponibili · degenza media ${o.degenza_media_giorni} gg`} />
        <Kpi icona={Pill} titolo="Spesa farmaci stimata" valore={euro(f.spesa_stimata)} testId="kpi-spesa" barra="bg-sky-600"
          dettaglio={f.farmaci_senza_prezzo ? `${f.farmaci_senza_prezzo} farmaci senza prezzo in anagrafica` : `${euro(f.spesa_per_ospite)} per ospite`} />
        <Kpi icona={HeartPulse} titolo="Cadute per 1000 giornate" valore={c.cadute_per_1000} testId="kpi-cadute" barra="bg-red-500"
          dettaglio={`${c.cadute} cadute · ${c.lesioni_nuove} lesioni insorte · ${c.contenzioni_attive} contenzioni`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Riquadro titolo="Andamento occupazione e giornate" nota="percentuale di occupazione per mese"
          testId="grafico-occupazione" className="lg:col-span-2">
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.andamento}>
                <defs>
                  <linearGradient id="gradOcc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0f766e" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0f766e" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis {...asseX} /><YAxis {...asseY} unit="%" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="occupazione_pct" name="Occupazione %" stroke="#0f766e" strokeWidth={2} fill="url(#gradOcc)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Riquadro>
        <Riquadro titolo="Movimenti del periodo" nota={`saldo ${m.saldo >= 0 ? '+' : ''}${m.saldo} ospiti`} testId="grafico-movimenti">
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={movimentiSerie} layout="vertical" margin={{ left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" {...asseY} />
                <YAxis type="category" dataKey="nome" tick={{ fontSize: 11, fill: '#64748b' }} width={92} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="valore" name="Ospiti" fill="#0284c7" radius={[0, 4, 4, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Riquadro>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Riquadro titolo="Farmaci piu consumati" nota="unita somministrate nel periodo" testId="grafico-top-farmaci">
          {f.top_farmaci.length === 0 ? (
            <p className="text-xs text-slate-400 py-8 text-center">Nessuna somministrazione registrata nel periodo</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={f.top_farmaci} layout="vertical" margin={{ left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" {...asseY} />
                  <YAxis type="category" dataKey="farmaco" tick={{ fontSize: 9, fill: '#64748b' }} width={140} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="unita" name="Unita" fill="#0f766e" radius={[0, 4, 4, 0]} barSize={12} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Riquadro>
        <Riquadro titolo="Consumi per categoria terapeutica" nota="unita somministrate per classe ATC" testId="grafico-categorie">
          {f.per_categoria.length === 0 ? (
            <p className="text-xs text-slate-400 py-8 text-center">Dati non disponibili per il periodo</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={f.per_categoria} dataKey="unita" nameKey="categoria" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {f.per_categoria.map((_, i) => <Cell key={i} fill={TORTA[i % TORTA.length]} />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Riquadro>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Riquadro titolo="Indicatori clinici per 1000 giornate" testId="box-clinici">
          <div className="space-y-2">
            {[['Cadute', c.cadute, c.cadute_per_1000], ['Eventi avversi', c.eventi_avversi, c.eventi_per_1000],
              ['Lesioni insorte', c.lesioni_nuove, c.lesioni_per_1000]].map(([nome, tot, indice]) => (
              <div key={nome} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                <span className="text-xs text-slate-600">{nome}</span>
                <span className="text-xs font-semibold text-slate-900 tabular-nums">{tot} <span className="text-slate-400 font-normal">· {indice}/1000</span></span>
              </div>
            ))}
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
              <span className="text-xs text-slate-600">Contenzioni attive</span>
              <span className="text-xs font-semibold text-slate-900 tabular-nums">{c.contenzioni_attive} <span className="text-slate-400 font-normal">· {c.contenzioni_prevalenza_pct}%</span></span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
              <span className="text-xs text-slate-600">Aderenza somministrazioni</span>
              <span className="text-xs font-semibold text-slate-900 tabular-nums">{num(f.aderenza_pct, '%')}</span>
            </div>
          </div>
        </Riquadro>
        <Riquadro titolo="Carico assistenziale stimato" nota="stima sui minuti standard per livello Barthel" testId="box-carico">
          <div className="space-y-3">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-slate-900 tabular-nums">{ca.minuti_per_ospite}</span>
              <span className="text-xs text-slate-500">minuti per ospite al giorno · {ca.ore_stimate_giorno} ore totali</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[['Dipendenza alta', ca.dipendenza_alta, 'text-red-600'], ['Media', ca.dipendenza_media, 'text-amber-600'],
                ['Autonomi', ca.autonomi, 'text-emerald-600']].map(([nome, v, colore]) => (
                <div key={nome} className="rounded-lg border border-slate-200 py-2">
                  <p className={`text-lg font-bold tabular-nums ${colore}`}>{v}</p>
                  <p className="text-[10px] text-slate-500">{nome}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500">
              Barthel medio {num(ca.barthel_medio)} · valutati {ca.barthel_valutati} su {ca.ospiti} ospiti
              ({ca.copertura_scale_pct}%) · {ca.braden_a_rischio} a rischio lesioni (Braden)
            </p>
          </div>
        </Riquadro>
        <Riquadro titolo="Cadute e somministrazioni per mese" testId="grafico-clinico-trend">
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.andamento}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis {...asseX} /><YAxis {...asseY} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Line type="monotone" dataKey="cadute" name="Cadute" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="somministrazioni" name="Somministrazioni" stroke="#0284c7" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Riquadro>
      </div>

      <Card data-testid="tabella-confronto-sedi">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-brand" />Confronto tra sedi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Sede</TableHead>
                <TableHead className="text-xs text-right">Presenti / posti</TableHead>
                <TableHead className="text-xs text-right">Occupazione</TableHead>
                <TableHead className="text-xs text-right">Giornate</TableHead>
                <TableHead className="text-xs text-right">Cadute /1000</TableHead>
                <TableHead className="text-xs text-right">Somministrazioni</TableHead>
                <TableHead className="text-xs text-right">Aderenza</TableHead>
                <TableHead className="text-xs text-right">Terapie attive</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.confronto_sedi.map(r => (
                <TableRow key={r.sede_id} data-testid={`riga-sede-${r.sede_id}`}>
                  <TableCell className="text-xs font-medium text-slate-800">{r.sede}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{r.presenti} / {r.posti_letto}</TableCell>
                  <TableCell className="text-xs text-right">
                    <Badge className={`text-[10px] ${r.occupazione_pct > 100 ? 'bg-red-100 text-red-700' : r.occupazione_pct >= 90 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {r.occupazione_pct}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{r.giornate_degenza.toLocaleString('it-IT')}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{r.cadute_per_1000}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{r.somministrazioni}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{num(r.aderenza_pct, '%')}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{r.terapie_attive}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" />
            La spesa farmaci usa i prezzi per confezione registrati nel dizionario farmaci; i farmaci senza prezzo non sono conteggiati.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 text-[11px] text-slate-400">
        <Users className="w-3.5 h-3.5" />Dati aggiornati al {dataIt(d.generato_il)}
      </div>

      <PrezziFarmaciDialog open={prezziAperti} onOpenChange={setPrezziAperti}
        farmaciSuggeriti={f.top_farmaci} onSalvato={carica} />
    </div>
  );
}
